import {
  AcceptInvitationPayload,
  ApiResponse,
  InvitationLookup,
} from "src/models";
import {
  InvitationSummary,
  ListInvitationsArgs,
  Page,
  StaffRole,
  toQueryString,
} from "src/models/staff";
import { apiSlice } from "src/store/apiSlice";

export interface InviteStaffPayload {
  email: string;
  role: StaffRole;
  /** Required for platform admins; others invite into their own district. */
  districtId?: string;
  schoolId?: string;
  classIds?: string[];
}

const base = "/api/account/v1/invitations";

/** Every invitation list, whatever its filters. */
const ALL_LISTS = { type: "Invitations" as const, id: "LIST" };

export const invitationsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getInvite: builder.query<InvitationLookup, string>({
      query: (token) => ({
        url: `${base}/${encodeURIComponent(token)}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<InvitationLookup>) =>
        response.data,
    }),
    acceptInvite: builder.mutation<ApiResponse, AcceptInvitationPayload>({
      query: (payload) => ({
        url: `${base}/accept`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Profile"],
    }),
    inviteStaff: builder.mutation<InvitationSummary, InviteStaffPayload>({
      query: (payload) => ({ url: base, method: "POST", body: payload }),
      transformResponse: (response: ApiResponse<InvitationSummary>) =>
        response.data,
      invalidatesTags: ["Staff", "Invitations"],
    }),
    listInvitations: builder.query<
      Page<InvitationSummary>,
      ListInvitationsArgs
    >({
      query: (args) => ({
        url: `${base}${toQueryString(args)}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<Page<InvitationSummary>>) =>
        response.data,
      providesTags: (_r, _e, args) => [
        ALL_LISTS,
        { type: "Invitations", id: `list:${args.districtId ?? "scope"}` },
      ],
    }),
    resendInvitation: builder.mutation<InvitationSummary, string>({
      query: (id) => ({ url: `${base}/${id}/resend`, method: "POST" }),
      transformResponse: (response: ApiResponse<InvitationSummary>) =>
        response.data,
      invalidatesTags: [ALL_LISTS],
    }),
    revokeInvitation: builder.mutation<InvitationSummary, string>({
      query: (id) => ({ url: `${base}/${id}/revoke`, method: "POST" }),
      transformResponse: (response: ApiResponse<InvitationSummary>) =>
        response.data,
      // A revoked invitee leaves the Staff list's "invited" rows.
      invalidatesTags: [ALL_LISTS, { type: "Staff", id: "LIST" }],
    }),
  }),
});

export const {
  useGetInviteQuery,
  useAcceptInviteMutation,
  useInviteStaffMutation,
  useListInvitationsQuery,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
} = invitationsApiSlice;
